import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, LOAN_PRODUCTS, LOAN_TYPE_LABEL } from "@/lib/api";
import { Save, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function DocConfig() {
  const qc = useQueryClient();
  const [loanType, setLoanType] = useState("personal_loan");
  const { data: cfg, refetch } = useQuery({
    queryKey: ["doc-cfg", loanType],
    queryFn: async () => (await api.get(`/doc-configs/${loanType}`)).data,
  });
  const [reqs, setReqs] = useState([]);
  useEffect(() => { setReqs(cfg?.requirements || []); }, [cfg]);

  const save = useMutation({
    mutationFn: async () => (await api.put(`/doc-configs/${loanType}`, { requirements: reqs })).data,
    onSuccess: () => { toast.success("Saved"); refetch(); qc.invalidateQueries({ queryKey: ["doc-config"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const update = (i, k, v) => setReqs(reqs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const remove = (i) => setReqs(reqs.filter((_, idx) => idx !== i));
  const add = () => setReqs([...reqs, { key: `new_doc_${reqs.length + 1}`, label: "New document", required: true, applies_to: "applicant" }]);

  return (
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Document Requirements</h1>
      <p className="text-sm text-[#52525B]">Configure which documents the Loan Junction team must collect for each loan product. Applies to public site listings and CRM upload dialog.</p>

      <div className="mt-6 bg-white border border-[#E4E4E7] rounded-xl p-6">
        <div className="grid sm:grid-cols-[220px_1fr] gap-4 items-end">
          <div>
            <Label>Loan product</Label>
            <Select value={loanType} onValueChange={setLoanType}>
              <SelectTrigger data-testid="doccfg-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LOAN_PRODUCTS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={add} className="text-sm font-semibold text-[#06152D] border border-[#E4E4E7] px-3 py-2 rounded-full inline-flex items-center gap-1.5 hover:bg-[#EBF2FF]" data-testid="doccfg-add"><Plus size={14} /> Add row</button>
            <button onClick={() => save.mutate()} disabled={save.isPending} className="bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="doccfg-save">
              <Save size={14} /> {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        <div className="mt-6 space-y-2">
          {reqs.map((r, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_120px_44px] gap-2 items-center" data-testid={`doccfg-row-${i}`}>
              <Input value={r.key} onChange={(e) => update(i, "key", e.target.value)} placeholder="key" />
              <Input value={r.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label" />
              <Select value={r.applies_to} onValueChange={(v) => update(i, "applies_to", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="applicant">Applicant</SelectItem><SelectItem value="co_applicant">Co-applicant</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
              </Select>
              <label className="inline-flex items-center gap-2 text-xs">
                <input type="checkbox" checked={r.required} onChange={(e) => update(i, "required", e.target.checked)} /> Required
              </label>
              <button onClick={() => remove(i)} className="text-red-600 hover:text-red-800" data-testid={`doccfg-remove-${i}`}><Trash2 size={16} /></button>
            </div>
          ))}
          {reqs.length === 0 && <div className="text-sm text-[#52525B]">No requirements set. Click "Add row" to start.</div>}
        </div>

        <div className="mt-6 text-xs text-[#52525B]">Preset defaults for <strong>{LOAN_TYPE_LABEL[loanType]}</strong> were seeded on first run. Edit freely — changes apply to both the public loan detail page and CRM upload dialogs.</div>
      </div>
    </div>
  );
}
